/*
 * Snooze and Unsnooze (master-inbox plan, step 3b), each with Undo through the page's toast. The
 * API answers with when each conversation slept before, so Undo puts each one back: awake, or
 * asleep until its old time. Snoozing a Done conversation takes it out of Done (the API does that),
 * so Undo marks those done again.
 */
import { appCount, appWeekdayTime } from "../../src/app-dates.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { inboxApi } from "./api.ts";
import { useInboxWrite } from "./inbox-actions.ts";
import type { Toast } from "./use-inbox.ts";

type SleptBefore = readonly { planId: string; until: string | null }[];

const conversations = (n: number) => `${appCount(n)} conversation${n === 1 ? "" : "s"}`;

/** Each conversation back to how it slept before, then the ones that were Done marked done. */
function putBack(workspaceId: string, previous: SleptBefore, wasDone: readonly string[]) {
  return async () => {
    const now = Date.now();
    const asleep = previous.filter((row) => row.until !== null && Date.parse(row.until) > now);
    const awake = previous.filter((row) => !asleep.includes(row)).map((row) => row.planId);
    if (awake.length) await inboxApi.unsnooze(workspaceId, awake);
    const byTime = new Map<string, string[]>();
    for (const row of asleep)
      if (row.until) byTime.set(row.until, [...(byTime.get(row.until) ?? []), row.planId]);
    for (const [until, ids] of byTime) await inboxApi.snooze(workspaceId, ids, until);
    if (wasDone.length) await inboxApi.done(workspaceId, wasDone, true);
  };
}

/** "2 conversations snoozed until Mon 19 Oct, 08:00. Taken out of Done." */
function snoozedText(count: number, until: string, wasDone: number): string {
  const done =
    wasDone === 0
      ? ""
      : wasDone === count
        ? " Taken out of Done."
        : ` ${wasDone} taken out of Done.`;
  return `${conversations(count)} snoozed until ${appWeekdayTime(until)}.${done}`;
}

/**
 * Snooze and Unsnooze through the inbox's write lifecycle (`inbox-actions.ts`): a refusal says
 * nothing changed, an outcome that could not be confirmed says so and the list shows the truth, and
 * Undo is a write of its own with its own failure (W5, F07).
 */
export function useSnoozeActions(workspaceId: string, onToast: (toast: Toast) => void) {
  const { write } = useInboxWrite(workspaceId, onToast);
  const undoing = (planIds: string[], previous: SleptBefore, wasDone: readonly string[]) => () =>
    write(
      "snooze",
      planIds,
      { verb: "undo it", noun: "undo" },
      putBack(workspaceId, previous, wasDone),
    );
  const snooze = async (rows: readonly InboxConversation[], until: string) => {
    if (!rows.length) return;
    const planIds = rows.map((row) => row.id);
    const wasDone = rows.filter((row) => row.doneAt !== null).map((row) => row.id);
    let previous: SleptBefore = [];
    const committed = await write(
      "snooze",
      planIds,
      { verb: "snooze", noun: "snooze" },
      async () => {
        previous = await inboxApi.snooze(workspaceId, planIds, until);
      },
    );
    if (!committed) return;
    onToast({
      text: snoozedText(rows.length, until, wasDone.length),
      undo: undoing(planIds, previous, wasDone),
    });
  };
  const unsnooze = async (rows: readonly InboxConversation[]) => {
    if (!rows.length) return;
    const planIds = rows.map((row) => row.id);
    let previous: SleptBefore = [];
    const committed = await write(
      "snooze",
      planIds,
      { verb: "unsnooze", noun: "unsnooze" },
      async () => {
        previous = await inboxApi.unsnooze(workspaceId, planIds);
      },
    );
    if (!committed) return;
    onToast({
      text: `${conversations(rows.length)} back in the inbox.`,
      undo: undoing(planIds, previous, []),
    });
  };
  return { snooze, unsnooze };
}
